import { useState } from 'react'
import Navbar from '../components/layout/Navbar'
import SummaryBar from '../components/prospects/SummaryBar'
import ProspectsToolbar from '../components/prospects/ProspectsToolbar'
import ProspectsTable from '../components/prospects/ProspectsTable'
import ProspectModal from '../components/prospects/ProspectModal'
import Pagination from '../components/prospects/Pagination'
import { useProspects } from '../hooks/useProspects'
import { enrichApi } from '../api/enrich'
import { prospectsApi } from '../api/prospects'

export default function ProspectsPage() {
  const {
    prospects, total, loading, error,
    filters, updateFilter,
    create, update, remove, exportCSV, refresh,
  } = useProspects()

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [enrichingIds, setEnrichingIds] = useState(new Set())
  const [enrichMsg, setEnrichMsg] = useState(null)
  const [scrapeProgress, setScrapeProgress] = useState(null)
  const [verifyProgress, setVerifyProgress] = useState(null)
  const [verifyAllProgress, setVerifyAllProgress] = useState(null)
  const [phoneProgress, setPhoneProgress] = useState(null)

  const openAdd = () => { setEditing(null); setModalOpen(true) }
  const openEdit = (p) => { setEditing(p); setModalOpen(true) }
  const closeModal = () => { setModalOpen(false); setEditing(null) }

  const handleSave = async (data) => {
    if (editing) await update(editing.id, data)
    else await create(data)
  }

  const handleDelete = async (p) => {
    if (!window.confirm(`Remove ${p.company} from prospects?`)) return
    await remove(p.id)
  }

  const handleSort = (key) => {
    if (filters.sort_by === key) {
      updateFilter('sort_dir', filters.sort_dir === 'desc' ? 'asc' : 'desc')
    } else {
      updateFilter('sort_by', key)
      updateFilter('sort_dir', 'desc')
    }
  }

  const handleEnrichOne = async (p) => {
    setEnrichingIds((prev) => new Set(prev).add(p.id))
    setEnrichMsg(null)
    try {
      const res = await enrichApi.enrichOne(p.id)
      const updated = res.data.updated_fields
      setEnrichMsg({
        type: 'success',
        text: updated.length > 0
          ? `✓ Enriched ${p.company} — updated: ${updated.join(', ')}`
          : `No new data found for ${p.company}`,
      })
      refresh()
    } catch (e) {
      setEnrichMsg({ type: 'error', text: `Failed to enrich ${p.company}` })
    } finally {
      setEnrichingIds((prev) => { const s = new Set(prev); s.delete(p.id); return s })
    }
  }

  const handleBulkScrape = async () => {
    setScrapeProgress('Starting scrape...')
    setEnrichMsg(null)
    try {
      const res = await enrichApi.scrapeAll()
      const { message, status } = res.data
      if (status && status.total === 0) {
        setEnrichMsg({ type: 'success', text: 'No prospects need scraping — all have emails already.' })
        setScrapeProgress(null)
        return
      }
      setEnrichMsg({
        type: 'success',
        text: `${message} — check back in a few minutes and refresh to see new emails.`,
      })
      setScrapeProgress(null)
      refresh()
    } catch (e) {
      setEnrichMsg({ type: 'error', text: e.response?.data?.detail || 'Bulk scrape failed' })
      setScrapeProgress(null)
    }
  }

  const handleVerifyEmails = async () => {
    const ids = prospects.map((p) => p.id)
    if (ids.length === 0) return
    setVerifyProgress('Verifying emails...')
    setEnrichMsg(null)
    try {
      const res = await enrichApi.outscraperVerifyBulk(ids)
      const { verified, risky, invalid, unknown, skipped } = res.data
      setEnrichMsg({
        type: 'success',
        text: `Email verify complete — ${verified} valid, ${risky} risky, ${invalid} invalid, ${unknown} unknown, ${skipped} skipped`,
      })
      refresh()
    } catch (e) {
      setEnrichMsg({ type: 'error', text: e.response?.data?.detail || 'Email verification failed' })
    } finally {
      setVerifyProgress(null)
    }
  }

  const handleVerifyAllEmails = async () => {
    if (!window.confirm('This will verify ALL unverified emails in the database. Continue?')) return
    setVerifyAllProgress('Verifying all emails...')
    setEnrichMsg(null)
    try {
      const res = await enrichApi.outscraperVerifyAll()
      const { verified, risky, invalid, unknown, skipped } = res.data
      setEnrichMsg({
        type: 'success',
        text: `Verify All complete — ${verified} valid, ${risky} risky, ${invalid} invalid, ${unknown} unknown, ${skipped} skipped`,
      })
      refresh()
    } catch (e) {
      setEnrichMsg({ type: 'error', text: e.response?.data?.detail || 'Verify All failed' })
    } finally {
      setVerifyAllProgress(null)
    }
  }

  const handleEnrichPhones = async () => {
    const ids = prospects.map((p) => p.id)
    if (ids.length === 0) return
    setPhoneProgress('Enriching phones...')
    setEnrichMsg(null)
    try {
      const res = await enrichApi.outscraperPhoneBulk(ids)
      const { enriched, mobile, skipped } = res.data
      setEnrichMsg({
        type: 'success',
        text: `Phone enrich complete — ${enriched} enriched, ${mobile} mobile, ${skipped} skipped`,
      })
      refresh()
    } catch (e) {
      setEnrichMsg({ type: 'error', text: e.response?.data?.detail || 'Phone enrichment failed' })
    } finally {
      setPhoneProgress(null)
    }
  }

  return (
    <div style={styles.root}>
      <Navbar />
      <main style={styles.main}>
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>Prospects</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              style={styles.sortToggleBtn}
              onClick={() => {
                updateFilter('sort_by', 'created_at')
                updateFilter('sort_dir', filters.sort_dir === 'desc' ? 'asc' : 'desc')
              }}
            >
              {filters.sort_dir === 'desc' ? '🕐 Newest First' : '🕰️ Oldest First'}
            </button>
            <button
              style={styles.bulkEnrichBtn}
              onClick={handleBulkScrape}
              disabled={!!scrapeProgress}
            >
              {scrapeProgress ? scrapeProgress : '🌐 Scrape websites'}
            </button>
            <button
              style={styles.verifyEmailBtn}
              onClick={handleVerifyEmails}
              disabled={!!verifyProgress}
            >
              {verifyProgress ? verifyProgress : '✉️ Verify Emails'}
            </button>
            <button
              style={styles.verifyAllBtn}
              onClick={handleVerifyAllEmails}
              disabled={!!verifyAllProgress}
            >
              {verifyAllProgress ? verifyAllProgress : '✉️ Verify All Emails'}
            </button>
            <button
              style={styles.enrichPhoneBtn}
              onClick={handleEnrichPhones}
              disabled={!!phoneProgress}
            >
              {phoneProgress ? phoneProgress : '📱 Enrich Phones'}
            </button>
          </div>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}
        {enrichMsg && (
          <div style={enrichMsg.type === 'success' ? styles.successBanner : styles.errorBanner}>
            {enrichMsg.text}
          </div>
        )}

        <SummaryBar prospects={prospects} />

        <ProspectsToolbar
          filters={filters}
          onFilter={updateFilter}
          onAdd={openAdd}
          onExport={exportCSV}
        />

        <div style={styles.tableCard}>
          <ProspectsTable
            prospects={prospects}
            loading={loading}
            onEdit={openEdit}
            onDelete={handleDelete}
            onEnrich={handleEnrichOne}
            enrichingIds={enrichingIds}
            sortBy={filters.sort_by}
            sortDir={filters.sort_dir}
            onSort={handleSort}
          />
        </div>

        <Pagination
          page={filters.page}
          pageSize={filters.page_size}
          total={total}
          onPage={(p) => updateFilter('page', p)}
        />
      </main>

      {modalOpen && (
        <ProspectModal
          prospect={editing}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </div>
  )
}

const styles = {
  root: { minHeight: '100vh', background: '#f5f5f3' },
  main: { maxWidth: 1280, margin: '0 auto', padding: '1.5rem' },
  pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' },
  pageTitle: { fontSize: 22, fontWeight: 500, color: '#1a1a1a' },
  sortToggleBtn: {
    background: '#f5f5f3', color: '#1a1a1a',
    border: '0.5px solid #ccc', borderRadius: 8,
    padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  bulkEnrichBtn: {
    background: '#E1F5EE', color: '#0F6E56',
    border: '0.5px solid #9FE1CB', borderRadius: 8,
    padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  verifyEmailBtn: {
    background: '#EEF2FF', color: '#3730A3',
    border: '0.5px solid #A5B4FC', borderRadius: 8,
    padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  verifyAllBtn: {
    background: '#F5F3FF', color: '#5B21B6',
    border: '0.5px solid #C4B5FD', borderRadius: 8,
    padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  enrichPhoneBtn: {
    background: '#FFF7ED', color: '#9A3412',
    border: '0.5px solid #FDB073', borderRadius: 8,
    padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
  errorBanner: {
    background: '#FCEBEB', color: '#791F1F',
    border: '0.5px solid #F09595',
    borderRadius: 8, padding: '10px 14px',
    fontSize: 13, marginBottom: '1rem',
  },
  successBanner: {
    background: '#EAF3DE', color: '#27500A',
    border: '0.5px solid #C0DD97',
    borderRadius: 8, padding: '10px 14px',
    fontSize: 13, marginBottom: '1rem',
  },
  tableCard: {
    background: '#fff',
    border: '0.5px solid #e0dfd8',
    borderRadius: 12,
    overflow: 'hidden',
  },
}